"use client";

import { motion } from "framer-motion";
import {
  Smartphone,
  Monitor,
  Globe,
  MessageCircle,
  Mail,
  Hash,
  Webhook,
  FileSpreadsheet,
  Database,
  ArrowRight,
  ArrowLeftRight,
  Plug,
  TabletSmartphone,
} from "lucide-react";
import { fadeUp, scaleIn, Section, agents } from "./utils";

const platforms = [
  {
    icon: Smartphone,
    name: "iPhone",
    status: "Available now",
    statusColor: "text-ok",
    dotColor: "bg-ok",
  },
  {
    icon: Monitor,
    name: "Web App",
    status: "Available now",
    statusColor: "text-ok",
    dotColor: "bg-ok",
  },
  {
    icon: TabletSmartphone,
    name: "Android",
    status: "Coming soon",
    statusColor: "text-content-muted",
    dotColor: "bg-content-muted",
  },
];

const channels = [
  { icon: MessageCircle, name: "WhatsApp", color: "#22C55E" },
  { icon: Mail, name: "Email", color: "#3B82F6" },
  { icon: Hash, name: "Slack", color: "#A78BFA" },
  { icon: MessageCircle, name: "SMS", color: "#DE7356" },
];

const integrations = [
  { icon: Globe, name: "REST APIs", desc: "Agents call your systems directly" },
  { icon: Webhook, name: "Webhooks", desc: "Trigger actions when things happen" },
  { icon: FileSpreadsheet, name: "Excel & CSV", desc: "Agents read and update your spreadsheets" },
  { icon: Database, name: "CRMs", desc: "Salesforce, HubSpot — agents keep them current" },
  { icon: Plug, name: "Custom Skills", desc: "Teach agents new tools specific to you" },
];

/*
 * Sequential channel flow animation.
 * Per channel cycle (5s):
 *   0.0s  Left channel neon glow activates
 *   0.5s  Message bubble travels left → crew (1.0s)
 *   1.5s  Crew neon glow activates, holds 1s (processing)
 *   2.5s  Reply bubble travels crew → right (1.0s)
 *   3.5s  Right channel neon glow activates
 *   4.0s  Pause before next channel
 * Total cycle = 4 channels × 5s = 20s
 */
const PHASE = 5;
const CYCLE = channels.length * PHASE;

const inboundMessages = [
  "Hi, is the 2BR still available?",
  "Need a quote for next week",
  "AC leaking in unit 1204",
  "Book a sedan for Friday",
];

function ChannelFlowDiagram() {
  const leftX = 10;
  const rightX = 580;
  const crewX = 255;
  const crewW = 190;
  const crewCx = crewX + crewW / 2;
  const crewCy = 90;

  return (
    <div className="relative w-full py-4">
      <svg className="w-full h-72 sm:h-64" viewBox="0 0 700 230" preserveAspectRatio="xMidYMid meet">
        {/* Neon glow filter */}
        <defs>
          <filter id="neonGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Static channel nodes — left */}
        {channels.map((ch, i) => {
          const y = 10 + i * 50;
          return (
            <g key={`left-${ch.name}`}>
              <motion.rect x={leftX} y={y} width="110" height="36" rx="8" fill="#232323" stroke={ch.color} strokeWidth="0.7"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 + i * 0.08 }} />
              <text x={leftX + 22} y={y + 23} fill={ch.color} fontSize="10">
                {ch.name === "WhatsApp" ? "💬" : ch.name === "Email" ? "📧" : ch.name === "Slack" ? "#" : "📱"}
              </text>
              <text x={leftX + 36} y={y + 23} fill="#FAFAFA" fontSize="10" fontWeight="500" fontFamily="system-ui, sans-serif">{ch.name}</text>

              {/* Left snake border — active when this channel fires */}
              <motion.rect x={leftX - 1} y={y - 1} width="112" height="38" rx="9" fill="none"
                stroke={ch.color} strokeWidth="2" strokeLinecap="round"
                strokeDasharray="75 225"
                filter="url(#neonGlow)"
                initial={{ strokeDashoffset: 300, opacity: 0 }}
                animate={{ strokeDashoffset: [300, 0], opacity: [0, 0.9, 0.9, 0] }}
                transition={{
                  strokeDashoffset: { duration: 1.2, delay: 1 + i * PHASE, repeat: Infinity, repeatDelay: CYCLE - 1.2, ease: "linear" },
                  opacity: { duration: 1.2, delay: 1 + i * PHASE, repeat: Infinity, repeatDelay: CYCLE - 1.2 },
                }} />
              <motion.rect x={leftX} y={y} width="110" height="36" rx="8" fill={ch.color}
                animate={{ opacity: [0, 0.06, 0.06, 0] }}
                transition={{ duration: 1.2, delay: 1 + i * PHASE, repeat: Infinity, repeatDelay: CYCLE - 1.2 }} />
            </g>
          );
        })}

        {/* Static channel nodes — right */}
        {channels.map((ch, i) => {
          const y = 10 + i * 50;
          return (
            <g key={`right-${ch.name}`}>
              <motion.rect x={rightX} y={y} width="110" height="36" rx="8" fill="#232323" stroke={ch.color} strokeWidth="0.7"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 + i * 0.08 }} />
              <text x={rightX + 22} y={y + 23} fill={ch.color} fontSize="10">
                {ch.name === "WhatsApp" ? "💬" : ch.name === "Email" ? "📧" : ch.name === "Slack" ? "#" : "📱"}
              </text>
              <text x={rightX + 36} y={y + 23} fill="#FAFAFA" fontSize="10" fontWeight="500" fontFamily="system-ui, sans-serif">{ch.name}</text>

              {/* Right snake border — fires when reply arrives */}
              <motion.rect x={rightX - 1} y={y - 1} width="112" height="38" rx="9" fill="none"
                stroke={ch.color} strokeWidth="2" strokeLinecap="round"
                strokeDasharray="75 225"
                filter="url(#neonGlow)"
                initial={{ strokeDashoffset: 300, opacity: 0 }}
                animate={{ strokeDashoffset: [300, 0], opacity: [0, 0.9, 0.9, 0] }}
                transition={{
                  strokeDashoffset: { duration: 1.2, delay: 1 + i * PHASE + 3.5, repeat: Infinity, repeatDelay: CYCLE - 1.2, ease: "linear" },
                  opacity: { duration: 1.2, delay: 1 + i * PHASE + 3.5, repeat: Infinity, repeatDelay: CYCLE - 1.2 },
                }} />
              <motion.rect x={rightX} y={y} width="110" height="36" rx="8" fill={ch.color}
                animate={{ opacity: [0, 0.06, 0.06, 0] }}
                transition={{ duration: 1.2, delay: 1 + i * PHASE + 3.5, repeat: Infinity, repeatDelay: CYCLE - 1.2 }} />
            </g>
          );
        })}

        {/* Connection lines (always visible, subtle) */}
        {channels.map((ch, i) => {
          const y = 10 + i * 50 + 18;
          return (
            <g key={`lines-${ch.name}`}>
              <line x1={leftX + 110} y1={y} x2={crewX} y2={crewCy} stroke={ch.color} strokeWidth="0.4" strokeDasharray="3 3" opacity="0.2" />
              <line x1={crewX + crewW} y1={crewCy} x2={rightX} y2={y} stroke={ch.color} strokeWidth="0.4" strokeDasharray="3 3" opacity="0.2" />
            </g>
          );
        })}

        {/* Center: AI Crew */}
        <motion.rect x={crewX} y="50" width={crewW} height="80" rx="16" fill="#232323" stroke="#DE7356" strokeWidth="1"
          initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", delay: 0.2 }} />
        <text x={crewCx} y={76} textAnchor="middle" fill="#FAFAFA" fontSize="12" fontWeight="600" fontFamily="system-ui, sans-serif">Your AI Crew</text>
        {agents.map((a, idx) => (
          <g key={a.name}>
            <defs><clipPath id={`crew-av-${idx}`}><circle cx={crewX + 38 + idx * 30} cy={105} r="10" /></clipPath></defs>
            <circle cx={crewX + 38 + idx * 30} cy={105} r="11" fill="#3D3D3D" />
            <image href={a.avatar} x={crewX + 38 + idx * 30 - 10} y={105 - 10} width="20" height="20"
              clipPath={`url(#crew-av-${idx})`} preserveAspectRatio="xMidYMid slice" />
          </g>
        ))}

        {/* Crew snake border — fires when message arrives, runs for 1.5s */}
        {channels.map((ch, i) => {
          // perimeter of rounded rect ≈ 2*(w+h) - 8*r + 2*π*r ≈ 2*(190+80) - 8*16 + 2*3.14*16 ≈ 540 - 128 + 100 ≈ 512
          const perim = 512;
          return (
            <g key={`crew-snake-${i}`}>
              {/* Snake stroke */}
              <motion.rect x={crewX - 1} y="49" width={crewW + 2} height="82" rx="17" fill="none"
                stroke={ch.color} strokeWidth="2" strokeLinecap="round"
                strokeDasharray={`${perim * 0.25} ${perim * 0.75}`}
                filter="url(#neonGlow)"
                initial={{ strokeDashoffset: perim, opacity: 0 }}
                animate={{
                  strokeDashoffset: [perim, 0],
                  opacity: [0, 0.9, 0.9, 0],
                }}
                transition={{
                  strokeDashoffset: { duration: 1.5, delay: 1 + i * PHASE + 1.5, repeat: Infinity, repeatDelay: CYCLE - 1.5, ease: "linear" },
                  opacity: { duration: 1.5, delay: 1 + i * PHASE + 1.5, repeat: Infinity, repeatDelay: CYCLE - 1.5 },
                }}
              />
              {/* Subtle fill glow */}
              <motion.rect x={crewX} y="50" width={crewW} height="80" rx="16" fill={ch.color}
                animate={{ opacity: [0, 0.05, 0.05, 0] }}
                transition={{ duration: 1.5, delay: 1 + i * PHASE + 1.5, repeat: Infinity, repeatDelay: CYCLE - 1.5 }} />
            </g>
          );
        })}

        {/* Animated message bubbles — one full sequence per channel */}
        {channels.map((ch, i) => {
          const y = 10 + i * 50 + 18;
          const start = 1 + i * PHASE;
          const msgText = inboundMessages[i];
          const bubbleW = 120;
          const bubbleH = 26;
          return (
            <g key={`bubbles-${ch.name}`}>
              {/* Inbound message bubble: left → lands at crew edge */}
              <motion.g
                initial={{ opacity: 0 }}
                animate={{
                  x: [leftX + 115, crewX - bubbleW + 10],
                  y: [y - bubbleH / 2, crewCy - bubbleH / 2],
                  opacity: [0, 1, 1, 1, 0],
                }}
                transition={{
                  duration: 1.0,
                  delay: start + 0.5,
                  repeat: Infinity,
                  repeatDelay: CYCLE - 1.0,
                  ease: "easeOut",
                  opacity: { duration: 1.3, times: [0, 0.1, 0.7, 0.9, 1] },
                }}
              >
                <rect x={0} y={0} width={bubbleW} height={bubbleH} rx="6" fill={ch.color} opacity="0.15" stroke={ch.color} strokeWidth="0.5" />
                <text x={10} y={16} fill={ch.color} fontSize="7" fontFamily="system-ui, sans-serif" opacity="0.9">
                  {msgText}
                </text>
              </motion.g>

              {/* Outbound reply bubble: crew → right channel */}
              <motion.g
                initial={{ opacity: 0 }}
                animate={{
                  x: [crewX + crewW - 10, rightX - bubbleW + 10],
                  y: [crewCy - bubbleH / 2, y - bubbleH / 2],
                  opacity: [0, 1, 1, 1, 0],
                }}
                transition={{
                  duration: 1.0,
                  delay: start + 2.5,
                  repeat: Infinity,
                  repeatDelay: CYCLE - 1.0,
                  ease: "easeOut",
                  opacity: { duration: 1.3, times: [0, 0.1, 0.7, 0.9, 1] },
                }}
              >
                <rect x={0} y={0} width={bubbleW} height={bubbleH} rx="6" fill="#DE7356" opacity="0.15" stroke="#DE7356" strokeWidth="0.5" />
                <text x={10} y={16} fill="#DE7356" fontSize="7" fontFamily="system-ui, sans-serif" opacity="0.9">
                  ✓ Handled by your crew
                </text>
              </motion.g>
            </g>
          );
        })}

        {/* Labels */}
        <text x={leftX + 55} y="222" textAnchor="middle" fill="#6B6B6B" fontSize="8" fontFamily="system-ui, sans-serif">Message comes in</text>
        <text x={crewCx} y="222" textAnchor="middle" fill="#6B6B6B" fontSize="8" fontFamily="system-ui, sans-serif">Your crew handles it</text>
        <text x={rightX + 55} y="222" textAnchor="middle" fill="#6B6B6B" fontSize="8" fontFamily="system-ui, sans-serif">Reply goes out</text>
      </svg>
    </div>
  );
}

export function Platforms() {
  return (
    <Section className="px-6 py-28 max-w-7xl mx-auto">
      <motion.div variants={fadeUp} className="text-center mb-16">
        <p className="text-sm font-medium text-coral-400 mb-3 uppercase tracking-wider">
          Connect with your favourite tools
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-content">
          Plugs right into your existing stack.
        </h2>
        <p className="mt-3 text-content-secondary max-w-lg mx-auto text-base">
          WhatsApp, Slack, your CRM, even your spreadsheets — your AI crew
          connects to the tools you already love and starts working alongside your team.
        </p>
      </motion.div>

      {/* Channel flow diagram — full width */}
      <motion.div
        variants={scaleIn}
        className="bg-surface-secondary/50 border border-surface-border rounded-2xl p-6 sm:p-8 mb-6"
      >
        <div className="flex items-center justify-center gap-2 mb-2">
          <ArrowLeftRight size={14} className="text-coral-400" />
          <p className="text-xs font-medium text-content-secondary uppercase tracking-wider">
            Two-way communication across every channel
          </p>
        </div>
        <ChannelFlowDiagram />
      </motion.div>

      {/* Two cards: Platforms + Integrations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Platforms card */}
        <motion.div
          variants={scaleIn}
          custom={0}
          className="bg-surface-secondary/50 border border-surface-border rounded-2xl p-8 hover:border-surface-hover transition-all duration-300"
        >
          <p className="text-[10px] uppercase tracking-widest font-mono text-content-muted mb-2">
            Available on every device
          </p>
          <p className="text-sm text-content-secondary leading-relaxed mb-6">
            Manage your crew from your pocket, your desk, or anywhere in between.
          </p>
          <div className="space-y-4">
            {platforms.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  custom={i * 0.5}
                  className="flex items-center gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-surface-tertiary/60 border border-surface-hover/40 flex items-center justify-center">
                    <Icon size={22} className="text-content" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-content">{p.name}</p>
                    <p className={`text-xs ${p.statusColor} flex items-center gap-1.5`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${p.dotColor}`} />
                      {p.status}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-6 pt-5 border-t border-surface-border/50">
            <a
              href="/signup"
              className="inline-flex items-center gap-1.5 text-sm text-coral-400 hover:text-coral-300 transition-colors"
            >
              Try it free
              <ArrowRight size={14} />
            </a>
          </div>
        </motion.div>

        {/* Integrations card */}
        <motion.div
          variants={scaleIn}
          custom={1}
          className="bg-surface-secondary/50 border border-surface-border rounded-2xl p-8 hover:border-surface-hover transition-all duration-300"
        >
          <p className="text-[10px] uppercase tracking-widest font-mono text-content-muted mb-2">
            Connect your existing tools
          </p>
          <p className="text-sm text-content-secondary leading-relaxed mb-6">
            Your agents connect to the systems you already rely on — CRMs,
            spreadsheets, APIs, and more. No switching, no migration.
          </p>
          <div className="space-y-3">
            {integrations.map((int_, i) => {
              const Icon = int_.icon;
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  custom={i * 0.3}
                  className="flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-lg bg-surface-tertiary/60 border border-surface-hover/30 flex items-center justify-center shrink-0">
                    <Icon size={14} className="text-content-muted" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-content">{int_.name}</p>
                    <p className="text-[11px] text-content-secondary">{int_.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </Section>
  );
}
